import { CLINIC_STORAGE_KEYS } from '../constants/buyer-portal';

import { getNativeAuth, isInNativeApp, nativeBridge } from './nativeBridge';
import { AuthTokenExpiredResponsePayload } from './types';

const AUTH_TOKEN_EXPIRED_TIMEOUT_MS = 10000;

/**
 * On app startup, copy the native-injected token/user into localStorage
 * so the existing axios interceptor and authStore hydration just work.
 * Idempotent — safe to call multiple times. Returns `true` when a new
 * access token was written (caller may rehydrate dependent stores), `false`
 * when nothing changed or the bridge isn't available.
 */
export function syncTokenFromNative(): boolean {
  if (!isInNativeApp()) return false;
  const auth = getNativeAuth();
  if (!auth?.accessToken) return false;

  try {
    const existingToken = localStorage.getItem(CLINIC_STORAGE_KEYS.TOKEN);
    const tokenChanged = existingToken !== auth.accessToken;
    if (tokenChanged) {
      localStorage.setItem(CLINIC_STORAGE_KEYS.TOKEN, auth.accessToken);
    }
    if (auth.refreshToken) {
      localStorage.setItem(CLINIC_STORAGE_KEYS.REFRESH_TOKEN, auth.refreshToken);
    }
    if (auth.user) {
      localStorage.setItem(CLINIC_STORAGE_KEYS.USER, JSON.stringify(auth.user));
    }
    return tokenChanged;
  } catch (err) {
    console.warn('[bridge/auth] syncTokenFromNative failed:', err);
    return false;
  }
}

/**
 * Ask native to refresh the expired access token. Returns the new access token
 * on success, or null if native says re-login required / call fails.
 * Updates localStorage on success so subsequent requests pick up the new token.
 */
export async function handleTokenExpired(
  requestUrl: string,
): Promise<string | null> {
  if (!isInNativeApp()) return null;

  try {
    const result = await nativeBridge.request<AuthTokenExpiredResponsePayload>(
      {
        type: 'AUTH_TOKEN_EXPIRED',
        payload: {
          statusCode: 401,
          requestUrl,
          canRetry: true,
        },
      },
      AUTH_TOKEN_EXPIRED_TIMEOUT_MS,
    );

    if (result?.requireRelogin) return null;
    if (result?.success && result.newToken) {
      try {
        localStorage.setItem(CLINIC_STORAGE_KEYS.TOKEN, result.newToken);
      } catch (err) {
        console.warn('[bridge/auth] persist refreshed token failed:', err);
      }
      return result.newToken;
    }
    return null;
  } catch (err) {
    console.warn('[bridge/auth] handleTokenExpired bridge call failed:', err);
    return null;
  }
}

/**
 * Tell native the user tapped "logout" inside the WebView.
 * Native is expected to clear SecureStore, unbind push token, and reload
 * its login screen. Resolves true on success, false on bridge failure.
 */
export async function notifyLogout(): Promise<boolean> {
  if (!isInNativeApp()) return false;
  try {
    const result = await nativeBridge.request<{ success: boolean }>(
      { type: 'AUTH_LOGOUT_FROM_WEB' },
      3000,
    );
    return !!result?.success;
  } catch (err) {
    console.warn('[bridge/auth] notifyLogout failed:', err);
    return false;
  }
}

